<template>
  <div
      :id="objectProps.Id"
      :data-elementindex="getElementIndex"
      :data-rotate="objectProps.RotateDegree"
      :data-translateX="objectProps.X"
      :data-translateY="objectProps.Y"
      :data-scalex="objectProps.scaleX"
      :data-scaley="objectProps.scaleY"
      :class="{ 'is-editing': isEditing }"
      :style="getStyles"
      data-type="table"
      class="position-absolute target table-object"
      @dblclick="onTableDblClick"
      @mousedown.capture="onTableMouseDown"
      @contextmenu.prevent="onRootContextMenu"
  >
    <table
        v-if="isFakeRendering"
        :style="pdfTableStyle"
        class="table-grid table-grid-pdf"
    >
      <colgroup>
        <col
            v-for="(col, c) in objectProps.cols"
            :key="'pdf-col-' + c"
            :style="{ width: (col.width || 0) + 'px' }"
        >
      </colgroup>
      <tr
          v-for="(row, r) in objectProps.rows"
          :key="'pdf-row-' + r"
          :style="{ height: (row.height || 0) + 'px' }"
      >
        <td
            v-for="(cell, c) in objectProps.cells[r]"
            v-if="cell && !cell.hidden"
            :key="'pdf-cell-' + r + '-' + c"
            :colspan="cell.colSpan > 1 ? cell.colSpan : null"
            :rowspan="cell.rowSpan > 1 ? cell.rowSpan : null"
            :style="pdfCellStyle(r, c)"
        >{{ cell.text }}</td>
      </tr>
    </table>

    <template v-else>
      <div
          :style="gridStyle"
          class="table-grid"
          @mousedown="onGridMouseDown"
      >
        <div
            v-for="(col, index) in objectProps.cols.slice(0, -1)"
            v-if="showResizeHandles"
            :key="'col-resize-' + index"
            :style="columnResizeStyle(index)"
            class="col-resizer"
            @mousedown.stop.prevent="startColumnResize($event, index)"
            @contextmenu.prevent.stop
        />

        <div
            v-for="(row, index) in objectProps.rows.slice(0, -1)"
            v-if="showResizeHandles"
            :key="'row-resize-' + index"
            :style="rowResizeStyle(index)"
            class="row-resizer"
            @mousedown.stop.prevent="startRowResize($event, index)"
            @contextmenu.prevent.stop
        />
        <div
            v-for="layer in cellFillLayers"
            :key="layer.key"
            :style="layer.style"
            class="cell-fill"
            aria-hidden="true"
        />
        <template v-for="(row, r) in objectProps.rows">
          <div
              v-for="(cell, c) in objectProps.cells[r]"
              v-if="cell && !cell.hidden"
              :key="'cell-' + r + '-' + c"
              :class="{
              'active-cell': isActiveCell(r, c),
              'range-selected': isInSelection(r, c),
            }"
              :style="cellStyle(r, c)"
              :data-row="r"
              :contenteditable="isCellEditing(r, c) ? true : null"
              class="cell"
              @input="onCellInput(r, c, $event)"
              @paste="onCellPaste(r, c, $event)"
              @mousedown="onCellMouseDown($event, r, c)"
              @click="onCellClick($event, r, c)"
              @dblclick="onCellDblClick($event, r, c)"
              @contextmenu.prevent.stop="openCellMenu($event, r, c)"
              @blur="onCellBlur(r, c, $event)"
              @keydown="onCellKeydown($event, r, c)"
              v-text="isCellEditing(r, c) ? undefined : cell.text"
          />
        </template>
      </div>
      <svg
          v-if="objectProps.showBorders"
          :style="borderSvgStyle"
          :width="totalWidth"
          :height="totalHeight"
          :viewBox="`0 0 ${totalWidth} ${totalHeight}`"
          class="table-borders"
          aria-hidden="true"
      >
        <line
            v-for="line in borderLines"
            :key="line.key"
            :x1="line.x1"
            :y1="line.y1"
            :x2="line.x2"
            :y2="line.y2"
            :stroke="line.spec.color"
            :stroke-width="line.drawnWidth"
            :stroke-dasharray="dashArray(line.spec.dash, line.drawnWidth)"
            :stroke-linecap="dashLinecap(line.spec.dash)"
        />
      </svg>
      <table-dots-overlay
          :visible="showTableDots"
          :col-x="dotPositions.colX"
          :col-y="dotPositions.colY"
          :row-x="dotPositions.rowX"
          :row-y="dotPositions.rowY"
          @col-click="onColDotClick"
          @row-click="onRowDotClick"
      />
      <table-context-menu
          ref="contextMenu"
          :visible="contextMenu.visible"
          :x="contextMenu.x"
          :y="contextMenu.y"
          :items="contextMenu.items"
          @select="onContextMenuSelect"
      />
    </template>
  </div>
</template>

<script>
import TableContextMenu from '@/components/CanvasShapes/TableContextMenu.vue';
import {
  fillCssBackground,
  shouldDrawVerticalLine,
  shouldDrawHorizontalLine,
  ensureSegmentBorders,
  getTableRowContentMinHeight,
  resizeTableTrack,
  TABLE_DEFAULT_BORDER_WIDTH,
  cloneBorderLine,
  cloneCellStyles
} from '@/components/DesignObjects/TableObject';
import {
  getTableScale,
  getTableContentScale,
  scaledMetrics,
  chromePx,
} from '@/components/DesignObjects/TableScale';
import EventBus from '@/EventBus';
import ApplicationEventTypes from '@/mixins/ApplicationEventTypes';
import TableDotsOverlay from '@/components/CanvasShapes/TableDotsOverlay.vue';

const TABLE_DRAG_THRESHOLD = 4;

export default {
  name:       'TablesComponent',
  components: { TableContextMenu, TableDotsOverlay },
  props:      {
    objectProps: {
      type:    Object,
      default: () => ({}),
    },
    elementIndex: {
      type:    Number,
      default: 0,
    },
    isGroupChild: {
      type:    Boolean,
      default: false,
    },
    isFakeRendering: {
      type:    Boolean,
      default: false,
    },
    displayControls: {
      type:    Boolean,
      default: false,
    },
  },
  data() {
    return {
      isEditing: false,
      sel:       {
        r1:    0, c1:    0, r2:    0, c2:    0, mode:  'cell', cells: [{ r: 0, c: 0 }],
      },
      activeCell:  { r: 0, c: 0 },
      editingCell: null,
      dragging:    false,
      contextMenu: {
        visible: false, x:       0, y:       0, items:   [],
      },
      dotPositions: {
        colX: 0, colY: 0, rowX: 0, rowY: 0,
      },
      resizing: null,
      isTransforming: false,
      dotsPositioned: false,
    };
  },
  computed: {
    TableScale() {
      return getTableScale(this.objectProps);
    },
    contentScale() {
      return getTableContentScale(this.objectProps);
    },
    metrics() {
      return scaledMetrics(this.contentScale);
    },
    selectionOutlinePx() {
      return chromePx(3, this.$store.getters.getZoomLevel);
    },
    resizeBarPx() {
      return chromePx(4, this.$store.getters.getZoomLevel);
    },
    selectedKeys() {
      return new Set(this.selectionCoords(this.sel).map(({ r, c }) => `${r}:${c}`));
    },
    borderLines() {
      void this.objectProps.borderEpoch;
      ensureSegmentBorders(this.objectProps);
      const cols = this.objectProps.cols || [];
      const rows = this.objectProps.rows || [];
      const v = this.objectProps.vBorders || [];
      const h = this.objectProps.hBorders || [];
      const lines = [];

      const colLeft = [0];
      cols.forEach((col, i) => {
        colLeft.push(colLeft[i] + (col.width || 0));
      });
      const rowTop = [0];
      rows.forEach((row, i) => {
        rowTop.push(rowTop[i] + (row.height || 0));
      });

      const pushSeg = (key, x1, y1, x2, y2, spec) => {
        if (!spec || Array.isArray(spec) || spec.dash === 'none') return;

        const w = this.strokeWidth(spec);
        lines.push({
          key, x1, y1, x2, y2, spec, drawnWidth: w,
        });
      };

      rows.forEach((row, r) => {
        cols.forEach((col, c) => {
          const x = colLeft[c];
          const y = rowTop[r];
          const w = col.width || 0;
          const ht = row.height || 0;
          const lastC = c === cols.length - 1;
          const lastR = r === rows.length - 1;

          pushSeg(`h-${r}-${c}`, x, y, x + w, y, h[r] && h[r][c]);
          pushSeg(`v-${c}-${r}`, x, y, x, y + ht, v[c] && v[c][r]);
          if (lastC) {
            pushSeg(`v-${c + 1}-${r}`, x + w, y, x + w, y + ht, v[c + 1] && v[c + 1][r]);
          }
          if (lastR) {
            pushSeg(`h-${r + 1}-${c}`, x, y + ht, x + w, y + ht, h[r + 1] && h[r + 1][c]);
          }
        });
      });

      return lines;
    },
    cellFillLayers() {
      void this.objectProps.fillEpoch;
      const layers = [];

      (this.objectProps.cells || []).forEach((row, r) => {
        (row || []).forEach((cell, c) => {
          if (!cell || cell.hidden) { return; }
          const background = fillCssBackground(cell.fill);
          if (!background) { return; }

          const style = {
            gridColumn:      c + 1,
            gridRow:         r + 1,
            zIndex:          1,
            backgroundColor: background,
          };
          if (cell.colSpan > 1) { style.gridColumnEnd = `span ${cell.colSpan}`; }
          if (cell.rowSpan > 1) { style.gridRowEnd = `span ${cell.rowSpan}`; }
          layers.push({ key: `fill-${r}-${c}`, style });
        });
      });

      return layers;
    },
    showTableDots() {
      const allowControls =
          this.displayControls ||
          (this.isGroupChild && this.objectProps.isSelected);
      return this.isEditing
          && !this.isFakeRendering
          && allowControls
          && !this.isTransforming
          && this.dotsPositioned;
    },
    showResizeHandles() {
      return (
          this.objectProps.isSelected
      );
    },
    getElementIndex() {
      if (this.isGroupChild) {
        return typeof this.elementIndex === 'number' ? this.elementIndex : -1;
      }
      return this.objectProps.elementIndex;
    },
    getStyles() {
      const {
        X, Y, RotateDegree, scaleX, scaleY, PdfSortOrder, IsVisible, locked,
      } = this.objectProps;
      return {
        transform:       `rotate(${RotateDegree}deg) scale(${scaleX},${scaleY})`,
        transformOrigin: 'top left',
        top:             `${Y}px`,
        left:            `${X}px`,
        width:           `${this.totalWidth}px`,
        height:          `${this.totalHeight}px`,
        zIndex:          PdfSortOrder,
        visibility:      IsVisible ? 'visible' : 'hidden',
        pointerEvents:   locked ? 'none' : 'all',
        overflow:        'visible',
      };
    },
    colWidths() {
      return (this.objectProps.cols || []).map(c => c.width || 0);
    },
    rowHeights() {
      return (this.objectProps.rows || []).map(r => r.height || 0);
    },
    totalWidth() {
      return this.colWidths.reduce((s, w) => s + w, 0);
    },
    totalHeight() {
      return this.rowHeights.reduce((s, h) => s + h, 0);
    },
    gridStyle() {
      const colWidths  = this.colWidths.map(w => `${w}px`).join(' ');
      const rowHeights = this.rowHeights.map(h => `${h}px`).join(' ');
      return {
        display:             'grid',
        gridTemplateColumns: colWidths,
        gridTemplateRows:    rowHeights,
        width:               '100%',
        height:              '100%',
        userSelect:          'none',
        boxSizing:           'border-box',
      };
    },
    borderColor() {
      return this.objectProps.borderColor || '#000000';
    },
    borderStroke() {
      return this.objectProps.borderWidth || TABLE_DEFAULT_BORDER_WIDTH;
    },
    borderStyle() {
      return this.objectProps.borderStyle
          || (this.objectProps.showBorders === false ? 'none' : 'all');
    },
    // Inline so borders stay overlaid even if scoped CSS is missing (PDF HTML export).
    borderSvgStyle() {
      return {
        position:      'absolute',
        left:          '0',
        top:           '0',
        width:         '100%',
        height:        '100%',
        pointerEvents: 'none',
        overflow:      'visible',
        zIndex:        5,
      };
    },
    pdfTableStyle() {
      return {
        width:          `${this.totalWidth}px`,
        height:         `${this.totalHeight}px`,
        borderCollapse: 'collapse',
        borderSpacing:  '0',
        tableLayout:    'fixed',
        boxSizing:      'border-box',
      };
    },
  },
  watch: {
    isEditing(val) {
      if (val) {
        this.$nextTick(() => this.updateDotPositions());
      } else {
        this.dotsPositioned = false;
      }
    },
    activeCell: {
      deep: true,
      handler() {
        this.$nextTick(() => this.updateDotPositions());
      },
    },
    'objectProps.X':      function () { this.updateDotPositions(); },
    'objectProps.Y':      function () { this.updateDotPositions(); },
    'objectProps.Width':  function () { this.updateDotPositions(); },
    'objectProps.Height': function () { this.updateDotPositions(); },
    'objectProps.scaleX': function () { this.updateDotPositions(); },
    'objectProps.scaleY': function () { this.updateDotPositions(); },
  },
  created() {
    this.edgeDrag  = null;
    this.dragWatch = null;
  },
  updated() {
    if (this.edgeDrag && this.edgeDrag.last) {
      this.cacheEdgeResizeNodes();
      this.paintEdgeResize();
    }
  },
  mounted() {
    if (!this.isFakeRendering) {
      document.addEventListener('mousedown', this.onDocMouseDown);
      document.addEventListener('keydown', this.onDocKeyDown);
      EventBus.$on('updateTableCellStyles', this.onUpdateTableCellStyles);
      EventBus.$on('tableEnterEditMode', this.onTableEnterEditMode);
      EventBus.$on('tableExitEditOnMove', this.onTableExitEditOnMove);
      EventBus.$on('tableStartCellEdit', this.onTableStartCellEdit);
      EventBus.$on('tableHideDots', this.onTableHideDots);
      EventBus.$on('tableShowDots', this.onTableShowDots);
      EventBus.$on('tableEdgeResizeStart', this.onTableEdgeResizeStart);
      EventBus.$on('tableEdgeResizePreview', this.onTableEdgeResizePreview);
      EventBus.$on('tableEdgeResizeEnd', this.onTableEdgeResizeEnd);
    }
    window.addEventListener('scroll', this.updateDotPositions, true);
    window.addEventListener('resize', this.updateDotPositions);
    this.$nextTick(() => {
      this.updateDotPositions();
      ensureSegmentBorders(this.objectProps);
    });
  },
  beforeDestroy() {
    this.stopTableDragWatch();
    if (!this.isFakeRendering) {
      document.removeEventListener('mousedown', this.onDocMouseDown);
      document.removeEventListener('keydown', this.onDocKeyDown);
      EventBus.$off('updateTableCellStyles', this.onUpdateTableCellStyles);
      EventBus.$off('tableExitEditOnMove', this.onTableExitEditOnMove);
      EventBus.$off('tableEnterEditMode', this.onTableEnterEditMode);
      EventBus.$off('tableStartCellEdit', this.onTableStartCellEdit);
      EventBus.$off('tableHideDots', this.onTableHideDots);
      EventBus.$off('tableShowDots', this.onTableShowDots);
      EventBus.$off('tableEdgeResizeStart', this.onTableEdgeResizeStart);
      EventBus.$off('tableEdgeResizePreview', this.onTableEdgeResizePreview);
      EventBus.$off('tableEdgeResizeEnd', this.onTableEdgeResizeEnd);
    }
    document.removeEventListener('mousemove', this.onCellMouseMove);
    document.removeEventListener('mouseup', this.onCellMouseUp);
    window.removeEventListener('scroll', this.updateDotPositions, true);
    window.removeEventListener('resize', this.updateDotPositions);
  },
  methods: {
    onTableHideDots(tableId) {
      if (tableId && tableId !== this.objectProps.Id) return;
      this.isTransforming = true;
      this.closeContextMenu();
    },
    dashArray(dash, width) {
      const w = Math.max(0.3, Number(width) || 0.3);
      if (dash === 'dash')       return `${w * 4} ${w * 0.75}`;
      if (dash === 'dash-short') return `${w * 2} ${w * 0.55}`;
      if (dash === 'dotted')     return `0 ${w * 2}`;
      return null;
    },
    dashLinecap(dash) {
      return dash === 'dotted' ? 'round' : 'butt';
    },
    onTableShowDots(tableId) {
      if (tableId && tableId !== this.objectProps.Id) return;
      this.isTransforming = false;
      this.$nextTick(() => this.updateDotPositions());
    },

    onTableEdgeResizeStart({ tableId, limits }) {
      if (tableId !== this.objectProps.Id || this.isFakeRendering) { return; }
      const cols = (this.objectProps.cols || []).map(c => c.width || 0);
      const rows = (this.objectProps.rows || []).map(r => r.height || 0);
      const fonts = [];
      (this.objectProps.cells || []).forEach((row) => {
        (row || []).forEach((cell) => {
          if (cell && !cell.hidden) { fonts.push(cell.fontSize || this.metrics.fontSize); }
        });
      });

      this.edgeDrag = {
        cols,
        rows,
        corner:   limits.corner,
        colIndex: limits.colIndex,
        rowIndex: limits.rowIndex,
        colMins:  limits.colMins,
        rowMins:  limits.rowMins,
        minRatio: limits.minRatio || 0,
        width:    cols.reduce((s, w) => s + w, 0),
        height:   rows.reduce((s, h) => s + h, 0),
        fonts,
        padY:     this.metrics.padY,
        padX:     this.metrics.padX,
        last:     null,
        nodes:    null,
      };
      this.cacheEdgeResizeNodes();
    },

    onTableEdgeResizePreview(preview) {
      const drag = this.edgeDrag;
      if (!drag || preview.tableId !== this.objectProps.Id) { return; }

      const { last } = drag;
      if (last
          && last.width === preview.width
          && last.height === preview.height
          && last.x === preview.x
          && last.y === preview.y) { return; }

      drag.last = {
        width:  preview.width,
        height: preview.height,
        x:      preview.x,
        y:      preview.y,
      };
      this.paintEdgeResize();
    },
    onTableEdgeResizeEnd(tableId) {
      if (tableId && tableId !== this.objectProps.Id) return;
      this.edgeDrag = null;
      this.$nextTick(() => {
        this.$forceUpdate();
        this.emitCellSelection();
      });
    },
    cacheEdgeResizeNodes() {
      const drag = this.edgeDrag;
      if (!drag || !this.$el || !this.$el.querySelector) { return; }
      const svg = this.$el.querySelector('.table-borders');

      drag.nodes = {
        grid:    this.$el.querySelector('.table-grid'),
        svg,
        lines:   svg ? svg.querySelectorAll('line') : [],
        colBars: this.$el.querySelectorAll('.col-resizer'),
        rowBars: this.$el.querySelectorAll('.row-resizer'),
        cells:   drag.corner ? this.$el.querySelectorAll('.table-grid > .cell') : null,
      };
    },
    paintEdgeResize() {
      const drag  = this.edgeDrag;
      const nodes = drag && drag.nodes;
      if (!nodes || !this.$el || !drag.last) { return; }

      const { cols, rows } = this.edgeResizeGeometry();
      const width  = cols.reduce((s, w) => s + w, 0);
      const height = rows.reduce((s, h) => s + h, 0);
      const { style } = this.$el;

      style.width  = `${width}px`;
      style.height = `${height}px`;
      style.left   = `${drag.last.x}px`;
      style.top    = `${drag.last.y}px`;

      if (nodes.grid) {
        nodes.grid.style.gridTemplateColumns = cols.map(w => `${w}px`).join(' ');
        nodes.grid.style.gridTemplateRows    = rows.map(h => `${h}px`).join(' ');
      }

      if (nodes.svg) {
        nodes.svg.setAttribute('width', width);
        nodes.svg.setAttribute('height', height);
        nodes.svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
      }

      if (nodes.lines && nodes.lines.length) {
        this.paintEdgeResizeBorderSegments(nodes.lines, cols, rows);
      }

      if (nodes.cells && nodes.cells.length) {
        this.paintEdgeResizeText(nodes.cells, this.edgeResizeRatio());
      }

      let x = 0;
      for (let i = 0; i < nodes.colBars.length; i += 1) {
        x += cols[i] || 0;
        nodes.colBars[i].style.left   = `${x - 3}px`;
        nodes.colBars[i].style.height = `${height}px`;
      }
      let y = 0;
      for (let i = 0; i < nodes.rowBars.length; i += 1) {
        y += rows[i] || 0;
        nodes.rowBars[i].style.top   = `${y - 3}px`;
        nodes.rowBars[i].style.width = `${width}px`;
      }
    },
    edgeResizeRatio() {
      const drag = this.edgeDrag;
      if (!drag || !drag.last) { return 1; }
      const wr    = drag.width ? (drag.last.width / drag.width) : 1;
      const hr    = drag.height ? (drag.last.height / drag.height) : 1;
      const ratio = Math.sqrt(Math.max(wr, 0) * Math.max(hr, 0)) || 1;
      return Math.max(drag.minRatio || 0, ratio);
    },
    edgeResizeGeometry() {
      const drag = this.edgeDrag;
      if (drag.corner) {
        const ratio = this.edgeResizeRatio();
        return {
          cols: drag.cols.map(w => w * ratio),
          rows: drag.rows.map(h => h * ratio),
        };
      }
      return {
        cols: resizeTableTrack(drag.cols, drag.colMins, drag.last.width, drag.colIndex, drag.corner),
        rows: resizeTableTrack(drag.rows, drag.rowMins, drag.last.height, drag.rowIndex, drag.corner),
      };
    },
    paintEdgeResizeText(cellEls, ratio) {
      const drag = this.edgeDrag;
      const padY = `${drag.padY * ratio}px`;
      const padX = `${drag.padX * ratio}px`;

      drag.fonts.forEach((size, i) => {
        const el = cellEls[i];
        if (!el) { return; }
        el.style.fontSize = `${size * ratio}px`;
        el.style.padding  = `${padY} ${padX}`;
      });
    },
    paintEdgeResizeBorderSegments(lineEls, cols, rows) {
      const v = this.objectProps.vBorders || [];
      const h = this.objectProps.hBorders || [];
      const colLeft = [0];
      cols.forEach((w, i) => { colLeft.push(colLeft[i] + w); });
      const rowTop = [0];
      rows.forEach((ht, i) => { rowTop.push(rowTop[i] + ht); });

      let i = 0;
      const setLine = (x1, y1, x2, y2, spec) => {
        if (!spec || Array.isArray(spec) || spec.dash === 'none') return;
        const line = lineEls[i];
        i += 1;
        if (!line) return;
        line.setAttribute('x1', x1);
        line.setAttribute('y1', y1);
        line.setAttribute('x2', x2);
        line.setAttribute('y2', y2);
      };

      rows.forEach((ht, r) => {
        cols.forEach((w, c) => {
          const x = colLeft[c];
          const y = rowTop[r];
          const lastC = c === cols.length - 1;
          const lastR = r === rows.length - 1;
          setLine(x, y, x + w, y, h[r] && h[r][c]);
          setLine(x, y, x, y + ht, v[c] && v[c][r]);
          if (lastC) setLine(x + w, y, x + w, y + ht, v[c + 1] && v[c + 1][r]);
          if (lastR) setLine(x, y + ht, x + w, y + ht, h[r + 1] && h[r + 1][c]);
        });
      });
    },

    paintBorderLines(lines, offset, sizes, axis, span) {
      const along  = axis;
      const across = axis === 'x' ? 'y' : 'x';
      const half   = this.borderStroke / 2;

      let cursor = 0;
      for (let i = 0; i <= sizes.length; i += 1) {
        const line = lines[offset + i];
        if (!line) { return; }
        if (i > 0) { cursor += sizes[i - 1]; }

        let at = i === 0 ? half : cursor;
        if (i === sizes.length) { at = Math.max(half, cursor - half); }

        line.setAttribute(`${along}1`, at);
        line.setAttribute(`${along}2`, at);
        line.setAttribute(`${across}1`, 0);
        line.setAttribute(`${across}2`, span);
      }
    },
    onTableStartCellEdit({ tableId, r, c }) {
      if (tableId !== this.objectProps.Id) { return; }
      if (this.isFakeRendering || this.objectProps.locked) { return; }
      this.startCellEdit(r, c);
    },
    onTableEnterEditMode(tableId) {
      if (tableId !== this.objectProps.Id) return;
      this.enterEditMode();
    },
    onTableExitEditOnMove(tableId) {
      if (tableId && tableId !== this.objectProps.Id) return;
      if (!this.isEditing) return;
      if (this.editingCell) {
        const editable = this.$el.querySelector('.cell[contenteditable="true"]');
        if (editable) editable.blur(); // saves text first
      }
      this.exitEditMode();
    },
    updateDotPositions() {
      if (!this.isEditing || this.isFakeRendering || !this.$el || this.isTransforming) {
        this.dotsPositioned = false;
        return;
      }
      const allowControls =
          this.displayControls ||
          (this.isGroupChild && this.objectProps.isSelected);
      if (!allowControls) {
        this.dotsPositioned = false;
        return;
      }

      const tableRect = this.$el.getBoundingClientRect();
      if (!tableRect.width || !tableRect.height) {
        this.dotsPositioned = false;
        return;
      }

      const c         = this.activeCell.c;
      const r         = this.activeCell.r;
      const colLeft   = this.getColLeft(c);
      const colWidth  = (this.objectProps.cols[c] && this.objectProps.cols[c].width) || 0;
      const rowTop    = this.getRowTop(r);
      const rowHeight = (this.objectProps.rows[r] && this.objectProps.rows[r].height) || 0;

      const sx = tableRect.width / this.totalWidth;
      const sy = tableRect.height / this.totalHeight;

      const colX = tableRect.left + (colLeft + colWidth / 2) * sx;
      const colY = tableRect.top - 28;
      const rowX = tableRect.left - 30;
      const rowY = tableRect.top + (rowTop + rowHeight / 2) * sy;

      if (!Number.isFinite(colX) || !Number.isFinite(rowY)) {
        this.dotsPositioned = false;
        return;
      }

      this.dotPositions = {
        colX, colY, rowX, rowY,
      };
      this.dotsPositioned = true;
    },
    pushTableUndo(label) {
      this.$nextTick(() => {
        EventBus.$emit('addIntoPageWiseUndoRedoArray', label);
      });
    },
    onColDotClick(e) {
      this.onRootContextMenu(e);
    },
    onRowDotClick(e) {
      this.onRootContextMenu(e);
    },
    getColLeft(c) {
      let x = 0;
      for (let i = 0; i < c; i++) {
        x += this.objectProps.cols[i].width || 0;
      }
      return x;
    },
    getRowTop(r) {
      let y = 0;
      for (let i = 0; i < r; i++) {
        y += this.objectProps.rows[i].height || 0;
      }
      return y;
    },
    onUpdateTableCellStyles(payload) {
      if (this.isFakeRendering) { return; }
      if (!payload || payload.tableId !== this.objectProps.Id) { return; }
      if (!this.isEditing) { this.isEditing = true; }

      if (payload.activeCell) {
        this.activeCell = { ...payload.activeCell };
      }
      if (payload.sel) {
        this.sel = { ...payload.sel };
      }

      if (this.editingCell) {
        const { r, c } = this.editingCell;
        const editable = this.$el.querySelector('.cell[contenteditable="true"]');
        if (editable) {
          this.objectProps.cells[r][c].text = this.sanitizeCellText(
              editable.innerText || editable.textContent || '',
          );
          this.editingCell = null;
          editable.blur();
        } else {
          this.editingCell = null;
        }
      }

      EventBus.$emit(ApplicationEventTypes.LeftPanelEvents.CLEAR_FONT_FAMILY_PREVIEW);
      const patch = payload.patch || {};
      this.applyStyleToSelection(patch, payload);
      const affectsLayout = ['fontSize', 'fontFamily', 'lineHeight', 'bold', 'italic']
          .some(key => Object.prototype.hasOwnProperty.call(patch, key));
      if (!affectsLayout) { return; }

      this.$nextTick(async () => {
        this.$forceUpdate();
        if (patch.fontFamily && document.fonts && document.fonts.load) {
          const size = patch.fontSize || 13;
          try {
            await document.fonts.load(`${size}px "${patch.fontFamily}"`);
          } catch (e) { /* ignore font load errors */ }
        }
        const rows = new Set(this.selectionCoords(this.sel).map(({ r }) => r));
        rows.forEach((r) => {
          this.fitRowHeight(r, { allowShrink: true });
        });
      });
    },
    columnResizeStyle(index) {
      let x = 0;

      for (let i = 0; i <= index; i++) {
        x += this.colWidths[i] || 0;
      }

      const bar = this.resizeBarPx;
      return {
        left:   `${x - (bar / 2)}px`,
        top:    0,
        width:  `${bar}px`,
        height: `${this.totalHeight}px`,
      };
    },

    rowResizeStyle(index) {
      let y = 0;

      for (let i = 0; i <= index; i++) {
        y += this.rowHeights[i] || 0;
      }

      const bar = this.resizeBarPx;
      return {
        top:    `${y - (bar / 2)}px`,
        left:   0,
        height: `${bar}px`,
        width:  `${this.totalWidth}px`,
      };
    },
    startColumnResize(e, index) {
      if (e.button !== 0) { return; }
      this.resizing = {
        type:       'col',
        index,
        startX:     e.clientX,
        startWidth: this.objectProps.cols[index].width,
      };

      document.addEventListener('mousemove', this.onResizeMove);
      document.addEventListener('mouseup', this.stopResize);
    },
    onResizeMove(e) {
      if (!this.resizing) { return; }

      if (this.resizing.type === 'col') {
        const dx = e.clientX - this.resizing.startX;

        this.objectProps.cols[this.resizing.index].width = Math.max(
            this.metrics.minColWidth,
            this.resizing.startWidth + dx,
        );
      }

      if (this.resizing.type === 'row') {
        const dy = e.clientY - this.resizing.startY;
        const contentMin = this.resizing.contentMin || this.metrics.minRowHeight;
        const next = Math.max(contentMin, this.resizing.startHeight + dy);
        this.objectProps.rows[this.resizing.index].height = next;
        this.$set(this.objectProps.rows[this.resizing.index], 'manualHeight', next > contentMin);
      }

      this.syncSize();
    },

    stopResize() {
      const resizing = this.resizing;
      document.removeEventListener('mousemove', this.onResizeMove);
      document.removeEventListener('mouseup', this.stopResize);
      this.resizing = null;
      this.syncSize();

      if (resizing && resizing.type === 'row') {
        const contentMin = getTableRowContentMinHeight(this.objectProps, resizing.index);
        const row = this.objectProps.rows[resizing.index];
        const height = (row && row.height) || contentMin;
        this.$set(row, 'manualHeight', height > contentMin);
      }
      if (resizing && resizing.type === 'col') {
        this.$nextTick(() => {
          (this.objectProps.rows || []).forEach((_, r) => {
            this.fitRowHeight(r, { allowShrink: false });
          });
        });
      }

      this.$nextTick(() => {
        EventBus.$emit(ApplicationEventTypes.ObjectSelectionEvents.UPDATED);
      });
      this.$nextTick(() => this.updateDotPositions());
      setTimeout(() => this.pushTableUndo('table: stop resize'), 20);
    },
    strokeWidth(spec) {
      if (!spec || Array.isArray(spec)) { return 0.3 * this.TableScale; }
      const slider = Math.max(1, Number(spec.width) || 1);
      return slider * 0.3 * this.TableScale;
    },
    startRowResize(e, index) {
      if (e.button !== 0) { return; }
      this.resizing = {
        type:        'row',
        index,
        startY:      e.clientY,
        startHeight: this.objectProps.rows[index].height,
        contentMin:  getTableRowContentMinHeight(this.objectProps, index),
      };

      document.addEventListener('mousemove', this.onResizeMove);
      document.addEventListener('mouseup', this.stopResize);
    },
    fitRowHeight(r, { allowShrink = true, liveTextByCol = null } = {}) {
      this.$nextTick(() => {
        const row = this.objectProps.rows[r];
        if (!row) { return; }

        const maxNeeded = getTableRowContentMinHeight(this.objectProps, r, liveTextByCol);
        const minH = this.metrics.minRowHeight;
        const current = row.height || minH;
        const canShrink = allowShrink && !row.manualHeight;
        const next = canShrink
            ? Math.max(minH, maxNeeded)
            : Math.max(minH, maxNeeded, current);

        if (next !== current) {
          this.objectProps.rows[r].height = next;
          this.syncSize();
          this.$nextTick(() => {
            EventBus.$emit(ApplicationEventTypes.ObjectSelectionEvents.UPDATED);
          });
        }
        this.$nextTick(() => this.updateDotPositions());
        setTimeout(() => this.pushTableUndo('table: cell text'), 20);
      });
    },
    syncSize() {
      this.objectProps.Width  = this.totalWidth;
      this.objectProps.Height = this.totalHeight;
      setTimeout(() => this.pushTableUndo('table: resize'), 20);
    },
    sanitizeCellText(text) {
      return String(text == null ? '' : text).replace(/\u200B/g, '');
    },
    onCellInput(r, c, e) {
      const target = e && e.target;
      const liveText = this.sanitizeCellText(
          (target && (target.innerText != null ? target.innerText : target.textContent)) || '',
      );
      if (this.objectProps.cells[r] && this.objectProps.cells[r][c]) {
        this.objectProps.cells[r][c].text = liveText;
      }
      this.fitRowHeight(r, {
        allowShrink: false,
        liveTextByCol: { [c]: liveText },
      });
    },
    insertPlainTextAtCaret(editable, text) {
      const sel = window.getSelection();
      if (!sel || !sel.rangeCount || !editable.contains(sel.anchorNode)) {
        editable.textContent = `${editable.textContent || ''}${text}`;
        return;
      }
      const range = sel.getRangeAt(0);
      range.deleteContents();
      const node = document.createTextNode(text);
      range.insertNode(node);
      range.setStartAfter(node);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
    },
    onCellPaste(r, c, e) {
      if (!this.isCellEditing(r, c)) { return; }
      e.preventDefault();
      const clipboard  = e.clipboardData || window.clipboardData;
      const raw        = clipboard ? clipboard.getData('text/plain') : '';
      const text       = this.sanitizeCellText(raw).replace(/\r\n?/g, '\n');
      const { target } = e;
      if (text) { this.insertPlainTextAtCaret(target, text); }
      this.onCellInput(r, c, { target });
    },
    enterEditMode() {
      if (this.isFakeRendering || this.objectProps.locked) { return; }
      this.isEditing = true;
      this.emitCellSelection();
    },
    onTableDblClick(e) {
      if (this.isGroupChild) {
        return;
      }
      e.stopPropagation();
      this.enterEditMode();
    },
    exitEditMode() {
      if (this.editingCell) {
        const editable = this.$el && this.$el.querySelector('.cell[contenteditable="true"]');
        if (editable) {
          const { r, c } = this.editingCell;
          this.objectProps.cells[r][c].text = this.sanitizeCellText(
              editable.innerText || editable.textContent || '',
          );
        }
      }
      this.isEditing   = false;
      this.editingCell = null;
      this.dotsPositioned = false;
      this.selectCell(this.activeCell.r, this.activeCell.c);
      this.closeContextMenu();
      EventBus.$emit('tableCellSelectionCleared');
    },
    cellStyle(r, c) {
      const cell   = this.objectProps.cells[r][c];
      const vAlign = cell.vAlign || 'middle';
      const hAlign = cell.align || 'center';
      const justifyContent = vAlign === 'top'
          ? 'flex-start'
          : (vAlign === 'bottom' ? 'flex-end' : 'center');
      const alignItems = hAlign === 'right'
          ? 'flex-end'
          : (hAlign === 'center' ? 'center' : 'stretch');
      // Borders are drawn by the SVG overlay; cells only carry selection chrome.
      const shadows = [];
      const outline = this.selectionOutlinePx;
      if (this.isActiveCell(r, c)) {
        // shadows.push('inset 0 0 0 3px rgb(59, 176, 250)');
        shadows.push(`inset 0 0 0 ${outline * 0.6}px rgb(139, 61, 255)`);
      } else if (this.isInSelection(r, c)) {
        // shadows.push('inset 0 0 0 2px rgba(59, 176, 250, 0.55)');
        shadows.push(`inset 0 0 0 ${outline * 0.6}px rgb(139, 61, 255)`);
      }
      const style = {
        gridColumn:     c + 1,
        gridRow:        r + 1,
        color:          cell.color || '#1f1f1f',
        fontFamily:     `'${(cell.fontFamily || 'Arial').replace(/'/g, '')}'`,
        lineHeight:     cell.lineHeight != null ? cell.lineHeight : 1.4,
        fontWeight:     cell.bold ? '700' : '400',
        fontStyle:      cell.italic ? 'italic' : 'normal',
        textDecoration: cell.underline ? 'underline' : 'none',
        fontSize:       `${cell.fontSize || this.metrics.fontSize}px`,
        textAlign:      hAlign,
        display:        'flex',
        flexDirection:  'column',
        justifyContent,
        alignItems,
        boxSizing:      'border-box',
        border:         'none',
        boxShadow:      shadows.length ? shadows.join(', ') : 'none',
        padding:        `${this.metrics.padY}px ${this.metrics.padX}px`,
        outline:        'none',
        overflow:       'hidden',
        whiteSpace:     'pre-wrap',
        wordBreak:      'break-word',
        cursor:         this.isEditing ? 'cell' : 'default',
        zIndex:         this.isActiveCell(r, c) ? 7 : (this.isInSelection(r, c) ? 6 : 2),
      };
      if (cell.colSpan > 1) { style.gridColumnEnd = `span ${cell.colSpan}`; }
      if (cell.rowSpan > 1) { style.gridRowEnd = `span ${cell.rowSpan}`; }
      return style;
    },
    pdfCellStyle(r, c) {
      const cell = this.objectProps.cells[r][c];
      if (!cell) { return {}; }
      const hAlign = cell.align || 'center';
      const vAlign = cell.vAlign || 'middle';
      const borderW = this.objectProps.showBorders ? (this.borderStroke || 1) : 0;
      const colSpan = cell.colSpan || 1;
      const rowSpan = cell.rowSpan || 1;
      let colW = 0;
      let rowH = 0;
      for (let i = 0; i < colSpan; i += 1) {
        colW += (this.objectProps.cols[c + i] && this.objectProps.cols[c + i].width) || 0;
      }
      for (let i = 0; i < rowSpan; i += 1) {
        rowH += (this.objectProps.rows[r + i] && this.objectProps.rows[r + i].height) || 0;
      }
      const v = this.objectProps.vBorders || [];
      const h = this.objectProps.hBorders || [];
      const cssEdge = (spec) => {
        if (!spec || spec.dash === 'none') return 'none';
        const style = spec.dash === 'dotted' ? 'dotted'
            : (spec.dash === 'dash' || spec.dash === 'dash-short') ? 'dashed'
                : 'solid';
        return `${this.strokeWidth(spec)}px ${style} ${spec.color || '#000'}`;
      };
      const edge = (on) => (on ? `${this.borderStroke}px solid ${this.borderColor}` : 'none');
      return {
        width:          `${colW}px`,
        height:         `${rowH}px`,
        color:          cell.color || '#1f1f1f',
        fontFamily:     `'${(cell.fontFamily || this.objectProps.fontFamily || 'Arial').replace(/'/g, '')}'`,
        lineHeight:     cell.lineHeight != null ? cell.lineHeight : 1.4,
        fontWeight:     cell.bold ? '700' : '400',
        fontStyle:      cell.italic ? 'italic' : 'normal',
        textDecoration: cell.underline ? 'underline' : 'none',
        fontSize:       `${cell.fontSize || this.metrics.fontSize}px`,
        textAlign:      hAlign,
        verticalAlign:  vAlign === 'top' ? 'top' : (vAlign === 'bottom' ? 'bottom' : 'middle'),
        boxSizing:      'border-box',
        padding:        `${this.metrics.padY}px ${this.metrics.padX}px`,
        overflow:       'hidden',
        whiteSpace:     'pre-wrap',
        wordBreak:      'break-word',
        background:     fillCssBackground(cell.fill) || 'transparent',
        borderTop:    cssEdge(h[r] && h[r][c]),
        borderBottom: cssEdge(h[r + rowSpan] && h[r + rowSpan][c]),
        borderLeft:   cssEdge(v[c] && v[c][r]),
        borderRight:  cssEdge(v[c + colSpan] && v[c + colSpan][r]),
      };
    },
    isActiveCell(r, c) {
      return this.isEditing && this.activeCell.r === r && this.activeCell.c === c;
    },
    isInSelection(r, c) {
      if (!this.isEditing) { return false; }
      return this.selectedKeys.has(`${r}:${c}`);
    },
    isCellEditing(r, c) {
      return this.editingCell && this.editingCell.r === r && this.editingCell.c === c;
    },
    selectCell(r, c) {
      this.setSelection([{ r, c }]);
    },

    setSelection(cells) {
      const list  = cells && cells.length ? cells : [{ r: 0, c: 0 }];
      const first = list[0];
      const last  = list[list.length - 1];

      this.sel        = {
        r1:    first.r, c1:    first.c, r2:    last.r, c2:    last.c, mode:  'cell', cells: list,
      };
      this.activeCell = { r: last.r, c: last.c };
      this.emitCellSelection();
      this.$nextTick(() => this.updateDotPositions());
    },

    // Shift-click: add the clicked cell to the selection, or drop it if already in.
    toggleCellInSelection(r, c) {
      const cells = this.selectionCoords(this.sel).slice();
      const at    = cells.findIndex(cell => cell.r === r && cell.c === c);

      if (at >= 0) {
        if (cells.length === 1) { return; }
        cells.splice(at, 1);
      } else {
        cells.push({ r, c });
      }

      this.setSelection(cells);
    },

    // Selected cells as {r, c}; older selections only carry the r1..r2/c1..c2 box.
    selectionCoords(sel) {
      const s = sel || this.sel || {};
      if (s.cells && s.cells.length) { return s.cells; }
      if (s.r1 == null) { return []; }

      const r1     = Math.min(s.r1, s.r2);
      const r2     = Math.max(s.r1, s.r2);
      const c1     = Math.min(s.c1, s.c2);
      const c2     = Math.max(s.c1, s.c2);
      const coords = [];
      for (let r = r1; r <= r2; r += 1) {
        for (let c = c1; c <= c2; c += 1) { coords.push({ r, c }); }
      }
      return coords;
    },

    selectionRows() {
      const rowCount = this.objectProps.rows.length;
      const rows     = this.selectionCoords(this.sel)
          .map(({ r }) => r)
          .filter(r => r >= 0 && r < rowCount);
      return Array.from(new Set(rows)).sort((a, b) => a - b);
    },
    selectionCols() {
      const colCount = this.objectProps.cols.length;
      const cols     = this.selectionCoords(this.sel)
          .map(({ c }) => c)
          .filter(c => c >= 0 && c < colCount);
      return Array.from(new Set(cols)).sort((a, b) => a - b);
    },
    shiftSelection(axis, from) {
      this.setSelection(this.selectionCoords(this.sel).map(({ r, c }) => (
          axis === 'row'
              ? { r: r >= from ? r + 1 : r, c }
              : { r, c: c >= from ? c + 1 : c }
      )));
    },

    // Saves the text of the cell being typed in, so switching cells never loses it.
    commitEditingCell() {
      if (!this.editingCell) { return; }
      const editable = this.$el && this.$el.querySelector('.cell[contenteditable="true"]');
      if (editable) {
        const { r, c } = this.editingCell;

        this.objectProps.cells[r][c].text = this.sanitizeCellText(
            editable.innerText || editable.textContent || '',
        );
        editable.blur();
      }
      this.editingCell = null;
    },

    applyStyleToSelection(patch, payload = {}) {
      const cells = this.resolveStyleTargetCells(payload);
      cells.forEach((cell) => {
        Object.keys(patch).forEach((key) => {
          this.$set(cell, key, patch[key]);
        });
      });
    },

    resolveStyleTargetCells(payload = {}) {
      const active = payload.activeCell || this.activeCell;
      const list   = [];
      this.selectionCoords(payload.sel || this.sel).forEach(({ r, c }) => {
        const cell = this.objectProps.cells[r] && this.objectProps.cells[r][c];
        if (cell && !cell.hidden) { list.push(cell); }
      });
      if (list.length) { return list; }

      if (active && active.r != null && active.c != null) {
        const cell = this.objectProps.cells[active.r]
            && this.objectProps.cells[active.r][active.c];
        if (cell && !cell.hidden) { return [cell]; }
      }

      return [];
    },

    emitCellSelection() {
      if (!this.isEditing) { return; }
      const cell = this.objectProps.cells[this.activeCell.r]
          && this.objectProps.cells[this.activeCell.r][this.activeCell.c];
      if (!cell) { return; }

      EventBus.$emit('tableCellSelectionChanged', {
        tableId:    this.objectProps.Id,
        sel:        { ...this.sel },
        activeCell: { ...this.activeCell },
        styles:     {
          color:      cell.color || '#1f1f1f',
          fontFamily: cell.fontFamily || 'Arial',
          fontSize:   cell.fontSize || this.metrics.fontSize,
          lineHeight: cell.lineHeight != null ? cell.lineHeight : 1.4,
          bold:       !!cell.bold,
          italic:     !!cell.italic,
          underline:  !!cell.underline,
          align:      cell.align || 'center',
          vAlign:     cell.vAlign || 'middle',
        },
      });
    },
    onCellClick(e, r, c) {
      if (this.isFakeRendering || this.objectProps.locked) { return; }
      if (e.shiftKey) {
        // Already handled on mousedown as a cell pick.
        if (this.isEditing) { e.stopPropagation(); }
        return;
      }

      if (this.isCellEditing(r, c)) {
        e.stopPropagation();
        return;
      }

      if (!this.objectProps.isSelected && !this.isEditing) {
        return;
      }
      e.stopPropagation();
      this.startCellEdit(r, c);
    },
    onGridMouseDown() {
      // keep edit mode
    },
    onTableMouseDown(e) {
      if (e.button !== 0 || this.isFakeRendering || this.objectProps.locked) { return; }
      if (!this.isEditing) { return; }
      const el = e.target;
      if (!el || !el.closest) { return; }
      if (el.closest('.col-resizer, .row-resizer, .table-dots-overlay, .table-context-menu')) { return; }
      // The cell being typed in keeps its own drag.
      if (el.closest('.cell[contenteditable="true"]')) { return; }
      this.dragWatch = { x: e.clientX, y: e.clientY };
      document.addEventListener('mousemove', this.onTableDragWatch);
      document.addEventListener('mouseup', this.stopTableDragWatch);
    },
    onTableDragWatch(e) {
      const start = this.dragWatch;
      if (!start) { return; }
      if (Math.abs(e.clientX - start.x) < TABLE_DRAG_THRESHOLD
          && Math.abs(e.clientY - start.y) < TABLE_DRAG_THRESHOLD) { return; }

      this.stopTableDragWatch();
      this.commitEditingCell();
      const selection = window.getSelection();
      if (selection) { selection.removeAllRanges(); }
      EventBus.$emit('tableDragFromInside', { tableId: this.objectProps.Id, inputEvent: e });
    },
    stopTableDragWatch() {
      this.dragWatch = null;
      document.removeEventListener('mousemove', this.onTableDragWatch);
      document.removeEventListener('mouseup', this.stopTableDragWatch);
    },
    onCellMouseDown(e, r, c) {
      if (e.button !== 0) { return; }
      if (e.shiftKey) {
        // Shift-click picks cells one at a time, like Canva.
        if (!this.isEditing) { return; }
        e.preventDefault();
        e.stopPropagation();
        this.commitEditingCell();
        this.toggleCellInSelection(r, c);
        return;
      }
      if (!this.isEditing) { return; }

      e.stopPropagation();
      if (this.contextMenu.visible) {
        this.closeContextMenu();
      }
      if (this.editingCell) {
        if (this.editingCell.r !== r || this.editingCell.c !== c) {
          const editable = this.$el.querySelector('.cell[contenteditable="true"]');
          if (editable) {
            const { r: er, c: ec } = this.editingCell;
            this.objectProps.cells[er][ec].text = this.sanitizeCellText(
                editable.innerText || editable.textContent || '',
            );
            this.editingCell = null;
            editable.blur();
          } else {
            this.editingCell = null;
          }
          this.selectCell(r, c);
        }
        return;
      }

      this.selectCell(r, c);
      this.dragging = true;
      document.addEventListener('mousemove', this.onCellMouseMove);
      document.addEventListener('mouseup', this.onCellMouseUp);
    },
    onCellMouseMove(e) {
      if (!this.dragging) { return; }
      const el = document.elementFromPoint(e.clientX, e.clientY);
      if (!el || !el.classList.contains('cell')) {}
    },
    onCellMouseUp() {
      this.dragging = false;
      document.removeEventListener('mousemove', this.onCellMouseMove);
      document.removeEventListener('mouseup', this.onCellMouseUp);
    },
    onCellDblClick(e, r, c) {
      if (this.isFakeRendering || this.objectProps.locked) { return; }
      if (this.isGroupChild && !this.objectProps.isSelected) {
        return;
      }
      e.stopPropagation();
      this.startCellEdit(r, c);
    },
    startCellEdit(r, c) {
      this.enterEditMode();
      this.selectCell(r, c);
      this.editingCell = { r, c };

      this.$nextTick(() => {
        const editable = this.$el.querySelector('.cell[contenteditable="true"]');
        if (!editable) { return; }
        const text = this.objectProps.cells[r][c].text || '';
        editable.textContent = text || '\u200B';

        editable.focus();
        const range = document.createRange();
        const sel   = window.getSelection();
        range.selectNodeContents(editable);
        range.collapse(false);
        sel.removeAllRanges();
        sel.addRange(range);
      });
    },
    onCellBlur(r, c, e) {
      if (!this.isCellEditing(r, c)) { return; }
      const target = e.target;
      let next = (target && (target.innerText != null ? target.innerText : target.textContent)) || '';
      next = this.sanitizeCellText(next);
      this.objectProps.cells[r][c].text = next;
      this.editingCell                  = null;
      this.pushTableUndo('table: cell text update');
      this.fitRowHeight(r, { allowShrink: true });
    },
    onCellKeydown(e, r, c) {
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Delete', 'Backspace', 'Enter', 'Tab'].includes(e.key)) {
        e.stopPropagation();
      }
      if (e.key === 'Escape') {
        e.target.blur();
        this.editingCell = null;
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        e.target.blur();
        this.editingCell = null;
        const nextR      = Math.min(r + 1, this.objectProps.rows.length - 1);
        this.selectCell(nextR, c);
      }
      if (e.key === 'Tab') {
        e.preventDefault();
        e.target.blur();
        this.editingCell = null;
        let nextC        = c + (e.shiftKey ? -1 : 1);
        let nextR        = r;
        if (nextC >= this.objectProps.cols.length) {
          nextC = 0;
          nextR = Math.min(r + 1, this.objectProps.rows.length - 1);
        }
        if (nextC < 0) {
          nextC = this.objectProps.cols.length - 1;
          nextR = Math.max(r - 1, 0);
        }
        this.selectCell(nextR, nextC);
      }
    },
    addRow(index, fromIndex = index) {
      const source      = this.objectProps.rows[fromIndex];
      const sourceCells = this.objectProps.cells[fromIndex] || [];
      const cols        = this.objectProps.cols.length;
      const row         = Array.from(
          { length: cols },
          (_, c) => cloneCellStyles(sourceCells[c], this.metrics.fontSize),
      );

      ensureSegmentBorders(this.objectProps);
      this.objectProps.rows.splice(index, 0, {
        height:       (source && source.height) || this.metrics.rowHeight,
        manualHeight: !!(source && source.manualHeight),
      });
      this.objectProps.cells.splice(index, 0, row);

      const hSrc = this.objectProps.hBorders[fromIndex] || [];
      this.objectProps.hBorders.splice(
          index,
          0,
          Array.from({ length: cols }, (_, c) => cloneBorderLine(hSrc[c])),
      );
      this.objectProps.vBorders.forEach((colSegs) => {
        colSegs.splice(index, 0, cloneBorderLine(colSegs[fromIndex]));
      });

      this.shiftSelection('row', index);
      this.syncSize();
      this.$nextTick(() => this.updateDotPositions());
      setTimeout(() => this.pushTableUndo('table: add row'), 20);
    },
    addCol(index, fromIndex = index) {
      const source = this.objectProps.cols[fromIndex];
      const insertAt = index + 1;

      ensureSegmentBorders(this.objectProps);
      this.objectProps.cols.splice(insertAt, 0, {
        width: (source && source.width) || this.metrics.colWidth,
      });
      this.objectProps.cells.forEach((row) => {
        row.splice(insertAt, 0, cloneCellStyles(row[fromIndex], this.metrics.fontSize));
      });

      const rowCount = this.objectProps.rows.length;
      const vSrc = this.objectProps.vBorders[fromIndex] || [];
      this.objectProps.vBorders.splice(
          insertAt,
          0,
          Array.from({length: rowCount}, (_, r) => cloneBorderLine(vSrc[r])),
      );
      this.objectProps.hBorders.forEach((rowSegs) => {
        if (Array.isArray(rowSegs)) {
          rowSegs.splice(insertAt, 0, cloneBorderLine(rowSegs[fromIndex]));
        }
      });

      this.shiftSelection('col', insertAt);
      this.syncSize();
      this.$nextTick(() => this.updateDotPositions());
      setTimeout(() => this.pushTableUndo('table: add column'), 20);
    },
    removeRowAt(index) {
      ensureSegmentBorders(this.objectProps);
      this.objectProps.rows.splice(index, 1);
      this.objectProps.cells.splice(index, 1);
      this.objectProps.hBorders.splice(index, 1);
      this.objectProps.vBorders.forEach((colSegs) => {
        colSegs.splice(index, 1);
      });
    },
    removeColAt(index) {
      ensureSegmentBorders(this.objectProps);
      this.objectProps.cols.splice(index, 1);
      this.objectProps.cells.forEach(row => row.splice(index, 1));
      this.objectProps.vBorders.splice(index, 1);
      this.objectProps.hBorders.forEach((rowSegs) => {
        if (Array.isArray(rowSegs)) {
          rowSegs.splice(index, 1);
        }
      });
    },
    delRows(indices) {
      const rows = Array.from(new Set(indices)).sort((a, b) => b - a);
      if (!rows.length || rows.length >= this.objectProps.rows.length) {
        return;
      }

      rows.forEach(index => this.removeRowAt(index));
      this.selectCell(
          Math.min(rows[rows.length - 1], this.objectProps.rows.length - 1),
          Math.min(this.activeCell.c, this.objectProps.cols.length - 1),
      );
      this.syncSize();
      this.$nextTick(() => this.updateDotPositions());
      setTimeout(() => this.pushTableUndo('table: delete rows'), 20);
    },
    delCols(indices) {
      const cols = Array.from(new Set(indices)).sort((a, b) => b - a);
      if (!cols.length || cols.length >= this.objectProps.cols.length) {
        return;
      }

      cols.forEach(index => this.removeColAt(index));
      this.selectCell(
          Math.min(this.activeCell.r, this.objectProps.rows.length - 1),
          Math.min(cols[cols.length - 1], this.objectProps.cols.length - 1),
      );
      this.syncSize();
      this.$nextTick(() => this.updateDotPositions());
      setTimeout(() => this.pushTableUndo('table: delete columns'), 20);
    },
    clearSelectionContent() {
      const rows = this.selectionRows();
      this.selectionCoords(this.sel).forEach(({r, c}) => {
        const cell = this.objectProps.cells[r] && this.objectProps.cells[r][c];
        if (cell && !cell.hidden) {
          cell.text = '';
        }
      });
      rows.forEach(r => this.fitRowHeight(r, {allowShrink: true}));
      this.$nextTick(() => this.updateDotPositions());
      setTimeout(() => this.pushTableUndo('table: clear content'), 20);
    },
    openCellMenu(e, r, c) {
      if (this.isFakeRendering || this.objectProps.locked) {
        return;
      }
      if (this.isGroupChild && !this.objectProps.isSelected) {
        return;
      }
      this.commitEditingCell();

      const keepSelection = this.isEditing && this.isInSelection(r, c);
      this.enterEditMode();
      if (!keepSelection) {
        this.selectCell(r, c);
      }

      const rows = this.selectionRows();
      const cols = this.selectionCols();

      this.contextMenu = {
        visible: true,
        x: e.clientX,
        y: e.clientY,
        items: [
          {id: 'insert-row-above', label: 'Insert row above', icon: 'arrowup'},
          {id: 'insert-row-below', label: 'Insert row below', icon: 'arrowdown'},
          {
            id: 'delete-row',
            label: rows.length > 1 ? `Delete ${rows.length} rows` : 'Delete row',
            icon: 'trash',
            disabled: rows.length >= this.objectProps.rows.length,
          },
          {id: 'divider-rows', divider: true},
          {id: 'insert-col-left', label: 'Insert column left', icon: 'arrowleft'},
          {id: 'insert-col-right', label: 'Insert column right', icon: 'arrowright'},
          {
            id: 'delete-col',
            label: cols.length > 1 ? `Delete ${cols.length} columns` : 'Delete column',
            icon: 'trash',
            disabled: cols.length >= this.objectProps.cols.length,
          },
          {id: 'divider-cols', divider: true},
          {id: 'clear-content', label: 'Clear content', icon: 'clearcontent'},
        ],
        row: r,
        col: c,
      };
    },
    onRootContextMenu(e) {
      if (this.isGroupChild && !this.objectProps.isSelected) {
        return;
      }
      if (!this.isEditing) {
        this.enterEditMode();
      }
      this.openCellMenu(e, this.activeCell.r, this.activeCell.c);
    },
    closeContextMenu() {
      this.contextMenu.visible = false;
      this.contextMenu.items = [];
    },
    onContextMenuSelect(actionId) {
      const {row, col} = this.contextMenu;
      const refRow = row != null ? row : this.activeCell.r;
      const refCol = col != null ? col : this.activeCell.c;
      const rows = this.selectionRows();
      const cols = this.selectionCols();
      const refRows = rows.length ? rows : [refRow];
      const refCols = cols.length ? cols : [refCol];
      const firstRow = refRows[0];
      const lastRow = refRows[refRows.length - 1];
      const firstCol = refCols[0];
      const lastCol = refCols[refCols.length - 1];

      switch (actionId) {
        case 'insert-row-above':
          this.addRow(firstRow, firstRow);
          break;
        case 'insert-row-below':
          this.addRow(lastRow + 1, lastRow);
          break;
        case 'delete-row':
          this.delRows(refRows);
          break;
        case 'insert-col-left':
          this.addCol(firstCol - 1, firstCol);
          break;
        case 'insert-col-right':
          this.addCol(lastCol, lastCol);
          break;
        case 'delete-col':
          this.delCols(refCols);
          break;
        case 'clear-content':
          this.clearSelectionContent();
          break;
        default:
          break;
      }
      this.closeContextMenu();
    },
    onDocMouseDown(e) {
      const el = e.target && e.target.nodeType === Node.TEXT_NODE
          ? e.target.parentElement
          : e.target;
      if (this.contextMenu.visible) {
        const menu = this.$refs.contextMenu && this.$refs.contextMenu.$el;
        if (menu && !menu.contains(el)) {
          this.closeContextMenu();
        }
        if (menu && menu.contains(el)) {
          return;
        }
      }
      if (el && el.closest && el.closest('.table-dots-overlay, .table-dot-btn')) {
        return;
      }
      if (!this.isEditing) {
        return;
      }
      if (this.$el.contains(e.target)) {
        return;
      }
      if (!el || !el.closest) {
        return;
      }
      if (!document.contains(el)) {
        return;
      }
      if (el.closest('.moveable-control, .moveable-area, #drag-button')) {
        return;
      }
      if (el.closest('.main-panel-wrapper, .main-widget-wrapper, .multiselect__content-wrapper, .max-z-index, .font-family-selection')) {
        return;
      }
      this.exitEditMode();
    },
    onDocKeyDown(e) {
      if (e.key === 'Escape') {
        this.closeContextMenu();
        if (this.editingCell) {
          this.editingCell = null;
        } else if (this.isEditing) {
          this.exitEditMode();
        }
      }
    },
  },
};
</script>

<style scoped>
.table-object {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  position: absolute;
}

.table-grid {
  width: 100%;
  height: 100%;
}

.table-borders {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: visible;
  z-index: 5;
}

.col-resizer {
  position: absolute;
  width: 4px;
  cursor: col-resize;
  background: transparent;
  z-index: 100;
}

.col-resizer:hover {
  background: #2196f3;
}

.row-resizer {
  position: absolute;
  height: 4px;
  cursor: row-resize;
  background: transparent;
  z-index: 100;
}

.row-resizer:hover {
  background: #2196f3;
}

.cell-fill {
  pointer-events: none;
}

.cell {
  position: relative;
  box-sizing: border-box;
}

.cell[contenteditable='true'] {
  cursor: text;
  user-select: text;
}
</style>
